/*
  Stored fit-guide outfit in an upsell row.
  ----------------------------------------
  The temp fit guide publishes the customer's chosen combination and its products to
  `localStorage['7mesh:fit-guide']` (see assets/temp-fit-guide.js). Every
  `blocks/_product-upsell.liquid` row on the site leads with that outfit instead of the
  products the merchant configured, and names the combination under its heading. With
  nothing stored the row is left exactly as Liquid rendered it.

  Cards are fetched, never built here: `/products/<handle>?section_id=upsell-card` returns
  the same snippets/upsell-card.liquid the row renders server-side, so there is no second
  card template to keep in step. The fetched card's dialog id names the product, so it is
  re-prefixed per row — two rows on one page would otherwise collide.

  Never in the theme editor. The merchant is editing the configured row, and swapping it
  for a customer's stored outfit would hide the blocks they are working on.

  Payload:
    { v: 2,
      config: { activity, condition, gender, degrees, band, summary },
      products: { mens: [{ slot, id, handle, title, url }], womens: [ … ] } }

  Both outfits are stored and the row serves the one matching the product it is on, which
  `_product-upsell` reads off the title's first word into `[data-upsell-gender]`. An
  accessory carries no prefix; that and a row off a product template fall back to
  `config.gender` — the gender the customer chose.

  Markup contract — see blocks/_product-upsell.liquid:
    [data-upsell]                  the row root, carrying the three format strings
    [data-upsell-heading]          heading text to swap in
    [data-upsell-for-format]       the combination line; %SUMMARY% is the stored summary
    [data-upsell-loading-label]    announced while the cards are in flight
    [data-upsell-gender]           mens | womens | empty — this product's own gender
    [data-upsell-for]              the combination line's element, hidden until filled
    .upsell__heading .typography   the heading the merchant set, rewritten in place
    .upsell__list                  the slide list, emptied and refilled
*/
(function () {
  if (window.__upsellFitGuide) return;
  window.__upsellFitGuide = true;

  var STORE_KEY = '7mesh:fit-guide';
  var SECTION = 'upsell-card';

  // Per page, not per row: the counter is what makes one product's card id unique
  // across two rows that both lead with the same outfit.
  var rows = 0;

  function designMode() {
    return !!(window.Shopify && window.Shopify.designMode);
  }

  function read() {
    var parsed;
    try {
      var raw = window.localStorage.getItem(STORE_KEY);
      parsed = raw ? JSON.parse(raw) : null;
    } catch (e) {
      // Private browsing, a full quota, or a value another tab corrupted. The row keeps
      // the products the merchant configured.
      return null;
    }
    // v1 held one flat list for the gender then on screen. Nothing to migrate — the guide
    // republishes as v2 on the next interaction, and until then the row is the merchant's.
    if (!parsed || parsed.v !== 2) return null;
    if (Object.prototype.toString.call(parsed.products) !== '[object Object]') return null;
    return parsed;
  }

  // Through carousel.js, never `__splide.destroy()`: that alone leaves `.is-initialized` on
  // the row, and `refresh()` skips anything wearing it, so the row would never mount again.
  function unmount(root) {
    var el = root.querySelector('.upsell__carousel');
    if (el && window.themeCarousel) window.themeCarousel.destroy(el);
  }

  function remount() {
    if (window.themeCarousel) window.themeCarousel.refresh();
  }

  /* The list matching the product on the page, then the one the customer chose. An accessory
     has no gender of its own, so that fallback is the common path, not an edge case. */
  function productsFor(payload, gender) {
    var lists = payload.products || {};
    var list = gender ? lists[gender] : null;
    if (!list || !list.length) list = lists[(payload.config || {}).gender];
    if (!list || !list.length) list = lists.mens;
    if (!list || !list.length) list = lists.womens;
    return list && list.length ? list : null;
  }

  function slide(child) {
    var el = document.createElement('div');
    el.className = 'splide__slide upsell__slide';
    el.appendChild(child);
    return el;
  }

  function skeleton() {
    var el = document.createElement('div');
    el.className = 'upsell__skel';
    return slide(el);
  }

  /* ---------- Fetch ---------- */

  // Rewritten as text, before parsing: the card id is also the radio group's `name` and
  // each option input's `id`, and one pass over the string covers every one of them.
  function card(handle, id, prefix) {
    var url = new URL('/products/' + handle, window.location.origin);
    url.searchParams.set('section_id', SECTION);

    return fetch(url.toString(), { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(function (r) {
        if (!r.ok) throw new Error('Upsell card request failed: ' + r.status);
        return r.text();
      })
      .then(function (html) {
        var base = 'upsell-fg-' + id;
        var doc = new DOMParser().parseFromString(html.split(base).join(base + '-' + prefix), 'text/html');
        var content = doc.querySelector('[data-upsell-fetched]');
        if (!content) throw new Error('Upsell card response had no card');
        return content.firstElementChild;
      });
  }

  /* ---------- Row ---------- */

  function fill(root, payload) {
    var list = root.querySelector('.upsell__list');
    if (!list) return;

    // Nothing to serve this row — leave the merchant's products where they are.
    var products = productsFor(payload, root.getAttribute('data-upsell-gender'));
    if (!products) return;

    // Claimed once. A second pass — a storage event, another selection — refills the row
    // it already owns rather than stacking a new set of cards behind the old one.
    if (root.__upsellPrefix === undefined) root.__upsellPrefix = ++rows;
    var prefix = root.__upsellPrefix;

    var heading = root.querySelector('.upsell__heading .typography');
    if (heading) {
      // Kept, so a failed fetch can put the merchant's own heading back.
      if (root.__upsellHeading === undefined) root.__upsellHeading = heading.innerHTML;
      heading.textContent = root.getAttribute('data-upsell-heading') || '';
    }

    var line = root.querySelector('[data-upsell-for]');
    if (line) {
      var summary = (payload.config && payload.config.summary) || '';
      var format = root.getAttribute('data-upsell-for-format') || '%SUMMARY%';
      line.textContent = summary ? format.replace('%SUMMARY%', summary) : '';
      line.hidden = !summary;
    }

    // The heading and the line above are cheap and always refresh. The cards are not:
    // a selection resolving to the same products — a temperature moved inside one band —
    // must not tear the row down and refetch what is already standing in it. Compared
    // against what the row was last *asked* for, never what is on screen: mid-request the
    // two differ, and matching on screen would skip the fetch that corrects it.
    var handles = products
      .map(function (p) {
        return p.handle;
      })
      .join(',');
    if (root.__upsellWanted === handles) return;
    root.__upsellWanted = handles;

    // Responses can land out of order, and the row must end on the newest selection
    // rather than the slowest request. Anything but the current token is stale.
    var token = (root.__upsellToken || 0) + 1;
    root.__upsellToken = token;

    // The configured row is kept whole rather than hidden in place: Splide counts a
    // hidden child as a slide, which would skew the overflow test and the paging.
    if (!root.__upsellRow) root.__upsellRow = Array.prototype.slice.call(list.children);

    unmount(root);
    list.textContent = '';
    for (var i = 0; i < products.length; i++) list.appendChild(skeleton());

    var status = document.createElement('p');
    status.className = 'visually-hidden';
    status.setAttribute('role', 'status');
    status.textContent = root.getAttribute('data-upsell-loading-label') || '';
    list.appendChild(status);

    Promise.all(
      products.map(function (p) {
        return card(p.handle, p.id, prefix);
      })
    )
      .then(function (cards) {
        if (token !== root.__upsellToken) return;
        list.textContent = '';
        for (var i = 0; i < cards.length; i++) {
          if (cards[i]) list.appendChild(slide(cards[i]));
        }
        remount();
      })
      .catch(function () {
        if (token !== root.__upsellToken) return;
        // Nothing arrived and there is nothing to show. The configured row is still the
        // right answer, so put it back rather than leaving the skeletons spinning.
        restore(root, list);
      });
  }

  // Bumps the token as well as clearing the wanted list: a request still in flight when
  // the row goes back to the merchant's products must not land on top of it.
  function restore(root, list) {
    root.__upsellWanted = null;
    root.__upsellToken = (root.__upsellToken || 0) + 1;

    var heading = root.querySelector('.upsell__heading .typography');
    if (heading && root.__upsellHeading !== undefined) heading.innerHTML = root.__upsellHeading;

    var line = root.querySelector('[data-upsell-for]');
    if (line) {
      line.textContent = '';
      line.hidden = true;
    }

    if (root.__upsellRow) {
      unmount(root);
      list.textContent = '';
      for (var i = 0; i < root.__upsellRow.length; i++) list.appendChild(root.__upsellRow[i]);
    }
    remount();
  }

  function apply(root, payload) {
    if (payload) {
      fill(root, payload);
      return;
    }
    // Only a row this script has already taken over has anything to give back.
    var list = root.querySelector('.upsell__list');
    if (list && root.__upsellRow) restore(root, list);
  }

  function applyAll(scope, payload) {
    if (designMode()) return;
    (scope || document).querySelectorAll('[data-upsell]').forEach(function (root) {
      apply(root, payload);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      applyAll(null, read());
    });
  } else {
    applyAll(null, read());
  }

  // The fit guide and an upsell row can share a page, so the row follows the selection
  // live rather than waiting for the next navigation.
  document.addEventListener('fit-guide:updated', function (e) {
    applyAll(null, e.detail || null);
  });

  // Another tab's selection. `storage` does not fire in the tab that wrote the value,
  // which is what the event above covers.
  window.addEventListener('storage', function (e) {
    if (e.key === STORE_KEY) applyAll(null, read());
  });

  document.addEventListener('shopify:section:load', function (e) {
    applyAll(e.target, read());
  });
})();
